import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { change, Field, formValueSelector } from 'redux-form';
import { useTranslation } from 'react-i18next';
import TickettypesField from '../TickettypesField';
import PricingsField from '../PricingsField';

/**
 * Resolve pricing ids allowed for a tickettype from field config.
 *
 * @param {string[]} pricingChoices
 * @param {string} tickettypeId
 * @returns {string[]|null} `null` = no filter (all pricings); array = allowlist (may be empty)
 */
export function pricingIdsAllowedForTickettype(pricingChoices, tickettypeId) {
    const raw = (pricingChoices || []).map(String).filter(Boolean);
    if (!raw.length) {
        return null;
    }
    const tt = String(tickettypeId || '');
    const scoped = [];
    const global = [];
    let hasScoped = false;
    raw.forEach((entry) => {
        const sep = entry.indexOf('::');
        if (sep === -1) {
            global.push(entry);
            return;
        }
        hasScoped = true;
        const entryTt = entry.slice(0, sep);
        const pricingId = entry.slice(sep + 2);
        if (entryTt === tt && pricingId) {
            scoped.push(pricingId);
        }
    });
    if (hasScoped) {
        return [...new Set([...scoped, ...global])];
    }
    return [...new Set(global)];
}

/**
 * Cascade tickettype → pricing under a single redux-form object path.
 *
 * @param {object} props
 * @param {string} props.name Base redux-form path (field id or group path)
 * @param {string} [props.formName] Redux-form name (formRenderer | formForm)
 * @param {object[]} props.tickettypes
 * @param {boolean} [props.withPricing]
 * @param {string[]} [props.pricingChoices] Bare pricing ids or `tickettypeId::pricingId`
 * @param {boolean} [props.required]
 */
const TickettypePricingFieldInput = ({
    name,
    formName = 'formRenderer',
    tickettypes,
    withPricing = true,
    pricingChoices = [],
    required = false,
    tickettypePlaceholder,
    pricingPlaceholder,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const selector = useMemo(() => formValueSelector(formName), [formName]);
    const tickettypeId = useSelector((state) => selector(state, `${name}.tickettype_id`));
    const pricingId = useSelector((state) => selector(state, `${name}.pricing_id`));

    const pricingAllowlist = useMemo(
        () => pricingIdsAllowedForTickettype(pricingChoices, tickettypeId),
        [pricingChoices, tickettypeId]
    );

    const pricings = useMemo(() => {
        if (!withPricing || !tickettypeId) {
            return [];
        }
        const type = (tickettypes || []).find((tt) => tt._id === tickettypeId);
        const allowSet = pricingAllowlist == null
            ? null
            : new Set(pricingAllowlist.map(String));
        const list = [];
        Object.keys(type?.pricings || {}).forEach((id) => {
            if (allowSet && !allowSet.has(String(id))) {
                return;
            }
            list.push({
                ...(type.pricings[id]),
                id,
            });
        });
        return list;
    }, [withPricing, tickettypeId, tickettypes, pricingAllowlist]);

    useEffect(() => {
        if (!withPricing) {
            if (pricingId != null && pricingId !== '') {
                dispatch(change(formName, `${name}.pricing_id`, null));
            }
            return;
        }
        if (!tickettypeId) {
            if (pricingId) {
                dispatch(change(formName, `${name}.pricing_id`, null));
            }
            return;
        }
        if (pricings.length === 1) {
            const onlyId = pricings[0].id;
            if (String(pricingId) !== String(onlyId)) {
                dispatch(change(formName, `${name}.pricing_id`, onlyId));
            }
            return;
        }
        if (pricingId && pricings.length && !pricings.some((p) => String(p.id) === String(pricingId))) {
            dispatch(change(formName, `${name}.pricing_id`, null));
        }
    }, [withPricing, tickettypeId, pricingId, pricings, dispatch, name, formName]);

    const onTickettypeChange = () => {
        dispatch(change(formName, `${name}.pricing_id`, null));
    };

    const showPricingSelect = withPricing && tickettypeId && pricings.length > 1;
    const autoPricing = withPricing && tickettypeId && pricings.length === 1;

    return (
        <div className="TickettypePricingFieldInput">
            <TickettypesField
                name={`${name}.tickettype_id`}
                tickettypes={tickettypes}
                single
                placeholder={tickettypePlaceholder || t('forms.tickettype_pricing_tickettype_placeholder')}
                onSelectChange={onTickettypeChange}
            />
            {required && (
                <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={tickettypeId || ''}
                    required
                    onChange={() => {}}
                    style={{
                        opacity: 0,
                        height: 0,
                        width: 0,
                        position: 'absolute',
                        pointerEvents: 'none',
                    }}
                    aria-hidden
                />
            )}
            {showPricingSelect && (
                <div className="mt-2">
                    <PricingsField
                        name={`${name}.pricing_id`}
                        pricings={pricings}
                        placeholder={pricingPlaceholder || t('forms.tickettype_pricing_pricing_placeholder')}
                    />
                    {required && (
                        <input
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={pricingId || ''}
                            required
                            onChange={() => {}}
                            style={{
                                opacity: 0,
                                height: 0,
                                width: 0,
                                position: 'absolute',
                                pointerEvents: 'none',
                            }}
                            aria-hidden
                        />
                    )}
                </div>
            )}
            {autoPricing && (
                <Field
                    component="input"
                    type="hidden"
                    name={`${name}.pricing_id`}
                />
            )}
        </div>
    );
};

export default TickettypePricingFieldInput;
